import { SEOHead } from "@/features/shared/SEOHead";
import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { Link } from "react-router-dom";
import { MissedCallCalculator } from "@/components/shared/MissedCallCalculator";
import { useRevealObserver } from "@/hooks/useRevealObserver";
import {
  PhoneMissed, Globe, Snowflake, Search, ArrowRight, ClipboardCheck,
  Bot, MessageSquare, Zap, Star, Database, Phone, LineChart,
  Car, Wrench, Trophy, Layers, Target, Megaphone, MonitorSmartphone, Workflow, MapPin,
  MousePointerClick, Share2, Puzzle,
} from "lucide-react";

/**
 * /start/: the discovery hub ("Start Here"). Pain-first triage that routes each
 * symptom to the page that solves it, plus free tools (lead magnets), the full
 * platform-spoke grid, solutions, and industries. Deliberately NOT an offer page:
 * offers live on their own pages. Nav "Start Here" points here (was
 * /website-launch-program/, which stays live as the website offer page).
 * Design register: matches the motion-passed homepage: mono eyebrows, tight
 * tracking, reveal cascades (prerender-safe via useRevealObserver).
 */

const pains = [
  {
    id: "pain-phones",
    num: "01",
    icon: PhoneMissed,
    short: "Missed calls",
    title: "The phone rings. Nobody picks up.",
    symptoms: [
      "Calls after 5 p.m. go straight to voicemail",
      "You're on a job or with a customer when it rings",
      "Callers don't leave messages. They call the next guy.",
    ],
    solution: "Frayze Reception, a managed AI receptionist",
    detail: "Answers every call 24/7, books the appointment, logs the caller. From $197/month.",
    link: "/platform/ai-receptionist/",
    cta: "Fix the phones",
    accent: "text-purple-700",
    accentBg: "bg-purple-700/10",
    hover: "hover:border-purple-700/30",
  },
  {
    id: "pain-website",
    num: "02",
    icon: Globe,
    short: "Dead website",
    title: "The website is old, slow, or embarrassing.",
    symptoms: [
      "Doesn't work right on phones",
      "Hasn't sent you a real lead in months",
      "You can't even edit it yourself",
    ],
    solution: "7-Day Website Launch",
    detail: "A fast, lead-capturing website live in 7 days. From $997, no contracts.",
    link: "/website-launch-program/",
    cta: "Fix the website",
    accent: "text-[#2F5FE8]",
    accentBg: "bg-[#2F5FE8]/10",
    hover: "hover:border-[#2F5FE8]/30",
  },
  {
    id: "pain-followup",
    num: "03",
    icon: Snowflake,
    short: "Cold follow-up",
    title: "Leads come in, then go cold.",
    symptoms: [
      "Inquiries live on sticky notes and in inboxes",
      "Follow-up happens when someone remembers",
      "Nobody knows which deals are slipping",
    ],
    solution: "Managed CRM & follow-up automation",
    detail: "Every lead captured, followed up in seconds, and worked until it closes.",
    link: "/solutions/crm-automation/",
    cta: "Fix the follow-up",
    accent: "text-teal-600",
    accentBg: "bg-teal-600/10",
    hover: "hover:border-teal-600/30",
  },
  {
    id: "pain-visibility",
    num: "04",
    icon: Search,
    short: "Invisible on Google",
    title: "Customers can't find you on Google.",
    symptoms: [
      "Competitors outrank you for your own trade",
      "A handful of old reviews, or none at all",
      "Your Google Business Profile is a ghost town",
    ],
    solution: "Local SEO & Reputation",
    detail: "Rank for the searches that matter and turn happy customers into reviews.",
    link: "/solutions/local-seo-reputation/",
    cta: "Fix the visibility",
    accent: "text-[#2F5FE8]",
    accentBg: "bg-[#2F5FE8]/10",
    hover: "hover:border-[#2F5FE8]/30",
  },
  {
    id: "pain-ads",
    num: "05",
    icon: MousePointerClick,
    short: "Ads not converting",
    title: "Paying for ads with nothing to show.",
    symptoms: [
      "Clicks and impressions, but the phone stays quiet",
      "No idea which ad actually made a customer call",
      "Reports full of charts you don't have time to decode",
    ],
    solution: "Managed Ad Campaigns",
    detail: "Ads wired to call tracking and the CRM. Every dollar traces to a lead, and the waste gets cut.",
    link: "/solutions/ad-management-scale/",
    cta: "Fix the ad spend",
    accent: "text-purple-700",
    accentBg: "bg-purple-700/10",
    hover: "hover:border-purple-700/30",
  },
  {
    id: "pain-marketing",
    num: "06",
    icon: Share2,
    short: "Social & listings idle",
    title: "Marketing runs only when you remember.",
    symptoms: [
      "The social feed has been quiet for months",
      "Google listing hours, photos, and posts are out of date",
      "Past customers never hear from you again",
    ],
    solution: "Managed marketing: social, listings, email & reviews",
    detail: "The recurring work runs on schedule without you touching it: posts, listings, campaigns, review requests.",
    link: "/solutions/",
    cta: "Put it on autopilot",
    accent: "text-teal-600",
    accentBg: "bg-teal-600/10",
    hover: "hover:border-teal-600/30",
  },
];

const capabilities = [
  { icon: Bot, name: "Frayze Reception", desc: "A managed AI receptionist. Every call answered and booked, 24/7, from $197/mo.", link: "/platform/ai-receptionist/" },
  { icon: MessageSquare, name: "Missed-Call Text-Back", desc: "A call gets missed, the caller gets an instant text, and the lead stays warm.", link: "/platform/missed-call-text-back/" },
  { icon: Zap, name: "Speed to Lead", desc: "New inquiries get a reply in seconds, before your competitors even see them.", link: "/platform/speed-to-lead/" },
  { icon: Star, name: "Reviews & Reputation", desc: "Review requests on autopilot, so happy customers become public proof.", link: "/platform/reputation-management/" },
  { icon: Database, name: "Database Reactivation", desc: "Wake up the leads and past customers already sitting in your list.", link: "/platform/database-reactivation/" },
  { icon: Phone, name: "Business Phone System", desc: "Calls, texts, and call tracking on one clean business line.", link: "/platform/business-phone-system/" },
];

const solutions = [
  { icon: Target, name: "AI Strategy & Rollout", link: "/solutions/ai-strategy-rollout/" },
  { icon: MonitorSmartphone, name: "Website Design", link: "/solutions/websites/" },
  { icon: MapPin, name: "Local SEO & Reputation", link: "/solutions/local-seo-reputation/" },
  { icon: Workflow, name: "CRM & Automation", link: "/solutions/crm-automation/" },
  { icon: Layers, name: "Lead Generation", link: "/solutions/lead-generation-system/" },
  { icon: Megaphone, name: "Ad Management", link: "/solutions/ad-management-scale/" },
];

const Eyebrow = ({ children, color = "text-[#2F5FE8]", bar = "bg-[#2F5FE8]" }: { children: React.ReactNode; color?: string; bar?: string }) => (
  <div className={`inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase ${color}`}>
    <span className={`w-6 h-[1px] ${bar}`} />
    {children}
  </div>
);

export default function StartHere() {
  useRevealObserver();

  return (
    <div className="min-h-screen bg-[#FBFAF7] font-instrument">
      <SEOHead
        title="Start Here: Find Where You're Losing Leads | Frayze"
        description="Every lead-driven business leaks somewhere: missed calls, a dead website, cold follow-up, or being invisible on Google. Find your leak, try the free tools, and see the fix."
        keywords="missed calls, website not getting leads, lead follow up, local seo, ai receptionist, small business lead generation, free website audit, missed call calculator"
        canonical="https://frayze.ca/start/"
      />
      <Header />

      <main className="pt-36 pb-0">
        {/* ============ HERO ============ */}
        <section className="max-w-[1312px] mx-auto px-6 pb-16 md:pb-24">
          <div className="max-w-3xl flex flex-col gap-5 text-left">
            <div className="reveal">
              <Eyebrow>Start here</Eyebrow>
            </div>
            <h1 className="reveal reveal-delay-1 text-4xl sm:text-5xl md:text-[60px] leading-[1.02] tracking-[-0.035em] font-semibold text-[#101319] text-pretty">
              Every business leaks leads somewhere.{" "}
              <span className="text-[#5A6270]">Where's yours?</span>
            </h1>
            <p className="reveal reveal-delay-2 text-lg md:text-xl leading-relaxed text-[#5A6270] text-pretty max-w-2xl">
              You don't need everything we build. You need the thing that's costing
              you jobs this month. Pick the problem that sounds like your week.
            </p>
          </div>

          {/* Quick-jump chips */}
          <div className="reveal reveal-delay-3 mt-10 flex flex-wrap gap-3">
            {pains.map((p) => (
              <a
                key={p.id}
                href={`#${p.id}`}
                className="inline-flex items-center gap-2.5 bg-white border border-[#E9E6DE] rounded-full pl-3 pr-4 py-2.5 text-sm font-semibold text-[#494F5C] hover:text-[#101319] hover:border-[#101319]/25 hover:shadow-sm transition-all"
              >
                <span className={`w-7 h-7 rounded-full ${p.accentBg} flex items-center justify-center`}>
                  <p.icon className={`w-3.5 h-3.5 ${p.accent}`} />
                </span>
                {p.short}
              </a>
            ))}
            <a
              href="#free-tools"
              className="inline-flex items-center gap-2 bg-[#101319] border border-[#101319] rounded-full px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2F5FE8] hover:border-[#2F5FE8] transition-all"
            >
              Just show me the free tools
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </section>

        {/* ============ PAIN TRIAGE ============ */}
        <section className="max-w-[1312px] mx-auto px-6 pb-16 md:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pains.map((p, i) => (
              <div
                key={p.id}
                id={p.id}
                className={`reveal ${i % 2 === 1 ? "reveal-delay-1" : ""} scroll-mt-28 group relative overflow-hidden bg-white border border-[#E9E6DE] ${p.hover} rounded-2xl p-8 md:p-10 flex flex-col gap-6 text-left lift-hover transition-all`}
              >
                <span
                  aria-hidden
                  className="absolute -top-3 right-6 font-mono-ibm text-[88px] leading-none font-medium text-[#101319]/[0.045] select-none"
                >
                  {p.num}
                </span>
                <div className={`w-12 h-12 rounded-xl ${p.accentBg} flex items-center justify-center`}>
                  <p.icon className={`w-6 h-6 ${p.accent}`} />
                </div>
                <h2 className="text-2xl md:text-[28px] font-semibold tracking-[-0.02em] text-[#101319]">
                  {p.title}
                </h2>
                <ul className="flex flex-col gap-2.5">
                  {p.symptoms.map((s) => (
                    <li key={s} className="flex items-start gap-3 text-sm md:text-base text-[#5A6270]">
                      <span className={`font-mono-ibm text-xs mt-1.5 ${p.accent}`}>·</span>
                      {s}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-5 border-t border-[#E9E6DE]/70">
                  <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#8A8F99] mb-1.5">The fix</div>
                  <div className="font-bold text-[#101319]">{p.solution}</div>
                  <div className="text-sm text-[#5A6270] mt-1 mb-4">{p.detail}</div>
                  <Link
                    to={p.link}
                    className={`inline-flex items-center gap-2 font-bold ${p.accent} transition-all group-hover:gap-3`}
                  >
                    {p.cta}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ============ FREE TOOLS (lead magnets) ============ */}
        <section id="free-tools" className="scroll-mt-20 bg-[#F4F2EC] border-y border-[#E9E6DE] py-16 md:py-28">
          <div className="max-w-[1312px] mx-auto px-6 flex flex-col gap-12">
            <div className="max-w-2xl flex flex-col gap-4 text-left">
              <div className="reveal">
                <Eyebrow>Free tools. No signup, no sales call</Eyebrow>
              </div>
              <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold text-[#101319]">
                Put a number on it first.
              </h2>
              <p className="reveal reveal-delay-2 text-base md:text-lg leading-relaxed text-[#5A6270]">
                See what the leak actually costs before you talk to anyone. Us included.
              </p>
            </div>

            <div className="reveal reveal-delay-2">
              <MissedCallCalculator />
            </div>

            <div className="grid grid-cols-1 gap-6">
              <Link
                to="/digital-audit/"
                className="reveal group bg-white border border-[#E9E6DE] rounded-2xl p-8 flex items-start gap-5 text-left hover:border-[#2F5FE8]/40 hover:shadow-lg lift-hover transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-[#2F5FE8]/10 flex items-center justify-center shrink-0">
                  <ClipboardCheck className="w-6 h-6 text-[#2F5FE8]" />
                </div>
                <div>
                  <div className="text-xl font-semibold tracking-[-0.02em] text-[#101319] mb-1.5">
                    Free 2-minute digital audit
                  </div>
                  <p className="text-sm md:text-base text-[#5A6270] mb-3">
                    Checks your website, Google presence, and lead capture, then tells
                    you the biggest hole and what fixing it looks like.
                  </p>
                  <span className="inline-flex items-center gap-2 font-bold text-[#2F5FE8] transition-all group-hover:gap-3">
                    Run the audit
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* ============ PLATFORM CAPABILITIES ============ */}
        <section className="max-w-[1312px] mx-auto px-6 py-16 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl flex flex-col gap-4 text-left">
              <div className="reveal">
                <Eyebrow color="text-purple-700" bar="bg-purple-700">The platform</Eyebrow>
              </div>
              <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold text-[#101319]">
                The capabilities, piece by piece.
              </h2>
              <p className="reveal reveal-delay-2 text-base md:text-lg leading-relaxed text-[#5A6270]">
                Each one works on its own. Together they're the system that catches
                every lead, managed for you.
              </p>
            </div>
            <Link
              to="/platform/"
              className="reveal reveal-delay-2 inline-flex items-center gap-2 font-bold text-purple-700 hover:text-purple-800 hover:gap-3 transition-all shrink-0"
            >
              See the full platform
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Hairline grid: one surface, ruled cells */}
          <div className="reveal reveal-delay-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E9E6DE] border border-[#E9E6DE] rounded-2xl overflow-hidden">
            {capabilities.map((c) => (
              <Link
                key={c.link}
                to={c.link}
                className="group bg-white p-8 flex flex-col gap-4 text-left hover:bg-[#FBFAF7] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-purple-700/10 flex items-center justify-center">
                    <c.icon className="w-5 h-5 text-purple-700" />
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#D8D4C9] group-hover:text-purple-700 group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="text-lg font-semibold tracking-[-0.01em] text-[#101319]">{c.name}</div>
                <p className="text-sm text-[#5A6270] leading-relaxed">{c.desc}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* ============ SOLUTIONS + INDUSTRIES ============ */}
        <section className="bg-[#101319] py-16 md:py-28">
          <div className="max-w-[1312px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
            <div className="text-left">
              <div className="mb-8 flex flex-col gap-4">
                <div className="reveal">
                  <Eyebrow color="text-[#7C9EFF]" bar="bg-[#7C9EFF]">Managed solutions</Eyebrow>
                </div>
                <h2 className="reveal reveal-delay-1 text-2xl sm:text-3xl md:text-[36px] leading-[1.06] tracking-[-0.03em] font-semibold text-white">
                  Done-for-you, end to end.
                </h2>
              </div>
              <div className="reveal reveal-delay-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {solutions.map((s) => (
                  <Link
                    key={s.link}
                    to={s.link}
                    className="group border border-white/10 hover:border-white/30 hover:bg-white/5 rounded-xl px-5 py-4 flex items-center gap-3.5 transition-all"
                  >
                    <s.icon className="w-5 h-5 text-[#7C9EFF] shrink-0" />
                    <span className="font-semibold text-[#F2F3F5] text-sm md:text-base">{s.name}</span>
                    <ArrowRight className="w-4 h-4 text-white/25 group-hover:text-[#7C9EFF] ml-auto transition-colors" />
                  </Link>
                ))}
              </div>
            </div>

            <div className="text-left">
              <div className="mb-8 flex flex-col gap-4">
                <div className="reveal">
                  <Eyebrow color="text-teal-400" bar="bg-teal-400">Built for your industry</Eyebrow>
                </div>
                <h2 className="reveal reveal-delay-1 text-2xl sm:text-3xl md:text-[36px] leading-[1.06] tracking-[-0.03em] font-semibold text-white">
                  Deepest where a missed lead hurts most.
                </h2>
              </div>
              <div className="reveal reveal-delay-2 flex flex-col gap-3">
                {[
                  {
                    icon: Car,
                    title: "Auto dealers, our flagship",
                    desc: "Built by an operator who ran the lead engine across six rooftops.",
                    link: "/automotive/",
                  },
                  {
                    icon: Wrench,
                    title: "Trades & contractors",
                    desc: "Every emergency call and quote request captured, even on the tools.",
                    link: "/industries/trades/",
                  },
                  {
                    icon: Trophy,
                    title: "See real client work",
                    desc: "What got built and what happened next.",
                    link: "/case-studies/",
                  },
                ].map((row) => (
                  <Link
                    key={row.link}
                    to={row.link}
                    className="group border border-white/10 hover:border-white/30 hover:bg-white/5 rounded-xl px-5 py-5 flex items-center gap-4 transition-all"
                  >
                    <row.icon className="w-6 h-6 text-teal-400 shrink-0" />
                    <div>
                      <div className="font-semibold text-[#F2F3F5]">{row.title}</div>
                      <div className="text-sm text-[#9BA3B2]">{row.desc}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/25 group-hover:text-teal-400 ml-auto shrink-0 transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Custom tool development: full-width strip */}
          <div className="max-w-[1312px] mx-auto px-6 mt-14">
            <div className="reveal border border-white/10 rounded-2xl p-8 md:p-10 grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-6 lg:gap-8 items-center">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                <Puzzle className="w-6 h-6 text-[#7C9EFF]" />
              </div>
              <div className="text-left">
                <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#9BA3B2] mb-1.5">
                  Custom tool development
                </div>
                <div className="text-xl md:text-2xl font-semibold tracking-[-0.02em] text-white mb-1.5">
                  Need something that doesn't exist off the shelf?
                </div>
                <p className="text-sm md:text-base text-[#9BA3B2] max-w-3xl">
                  Quote calculators, customer portals, booking flows, inventory tools,
                  integrations between the systems you already run. We build them on
                  the same platform, so they feed your CRM instead of another silo.
                </p>
              </div>
              <Link
                to="/solutions/ai-strategy-rollout/"
                className="inline-flex items-center justify-center gap-2 font-bold text-[#7C9EFF] hover:text-white border border-white/20 hover:border-white/50 px-6 py-3.5 rounded-xl transition-all shrink-0"
              >
                Scope a custom build
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ============ CLOSING CTA ============ */}
        <section className="max-w-[1312px] mx-auto px-6 py-16 md:py-28">
          <div className="reveal grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center bg-white border border-[#E9E6DE] rounded-2xl p-8 md:p-14">
            <div className="flex flex-col gap-4 text-left">
              <div className="font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8]">
                Still not sure?
              </div>
              <h2 className="text-2xl md:text-[36px] leading-[1.06] tracking-[-0.03em] font-semibold text-[#101319]">
                Fifteen minutes. We'll find the leak together.
              </h2>
              <p className="text-base md:text-lg text-[#5A6270] max-w-2xl">
                A straight look at your setup. We'll name the biggest hole and tell
                you honestly whether we're the right fix. No contracts either way.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-4 shrink-0">
              <Link
                to="/book-consultation/"
                className="text-center bg-[#2F5FE8] hover:bg-[#2049BE] text-white font-bold px-8 py-4 rounded-xl shadow-lg shadow-[#2F5FE8]/25 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                Book a 15-min call
              </Link>
              <Link
                to="/pricing/"
                className="text-center border border-[#D8D4C9] hover:border-[#101319]/40 text-[#101319] font-bold px-8 py-4 rounded-xl transition-all hover:scale-[1.02]"
              >
                See pricing
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
